import {
  type Block,
  type BlockRelationship,
  type CommonBRsCompoundIndexes,
  type CommonBlocksCompoundIndexes,
  type RootBlock,
  blockSchema,
  blockBaseOpsInternal,
  commonBlocksCompoundIndexes,
  getChildrenKeys,
  compareOrder,
  getOnChangeHandler,
  isBRInRoot,
  isRootBlock,
  commonBRsCompoundIndexes,
  getOnIndexConflictHandler,
  isVirtualInstanceChildTarget,
  isBRMoreImportant,
} from '@basehub/data/replicache/index'
import {
  type AtLeast,
  type TreeManager,
  getTreeManager,
} from '@basehub/utils/tree/index'
import { invariant } from '@epic-web/invariant'
import type {
  ReadTransaction,
  ReadonlyJSONObject,
  ReadonlyJSONValue,
  WriteTransaction,
} from 'replicache'
import { z } from 'zod'
import { sql } from 'drizzle-orm'
import { createStore } from 'zustand/vanilla'
import { ReplicacheTransaction } from './backend'
import {
  type DB,
  type Entry,
  getNestedBlocksSQL,
  schema,
} from '@basehub/data/db/index'
import { type ClientOnlyParams, type ServerOnlyParams } from './spec'
import {
  type IndexedMap,
  type IndexedMapCompoundIndex,
  createIndexedMap,
} from '@basehub/utils/indexed-map/index'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import {
  RevalidateBlockMilestoneParams,
  revalidateBlockMilestone,
} from '../mutators/repo-space/block/helpers/revalidate-milestone'
import pMap from 'p-map'

export const transactionClientContext = createStore<
  Map<
    // spaceid
    string,
    ClientOnlyParams
  >
>(() => new Map())

type ReplaceFirstParameterWithBaseHubTransaction<
  T extends Record<string, (...args: any[]) => any>,
> = {
  [K in keyof T]: T[K] extends (tx: any, ...args: infer P) => any
    ? (tx: BaseHubTransaction, ...args: P) => ReturnType<T[K]>
    : never
}

const getEntriesOptionsSchema = z.object({
  rootBlockId: z.string(),
})

export const blockBaseOps: Omit<
  ReplaceFirstParameterWithBaseHubTransaction<typeof blockBaseOpsInternal>,
  | 'put'
  | 'mustGet'
  | 'update'
  | 'set'
  | 'listIDs'
  | 'listEntries'
  | 'list'
  | 'get'
> & {
  get: (
    tx: BaseHubTransaction,
    key: string,
    opts?: { acceptDeleted?: boolean }
  ) => ReturnType<(typeof blockBaseOpsInternal)['get']>
  put: (
    tx: BaseHubTransaction,
    value: Block,
    parentKey: string | null
  ) => ReturnType<(typeof blockBaseOpsInternal)['set']>
  update: (
    tx: BaseHubTransaction,
    value: AtLeast<Block, 'id'>,
    parentKey?: string
  ) => ReturnType<(typeof blockBaseOpsInternal)['set']>
  list: (
    tx: BaseHubTransaction,
    options: Parameters<(typeof blockBaseOpsInternal)['list']>[1] & {
      getEntriesOptions: z.infer<typeof getEntriesOptionsSchema>
    },
    ...params: any
  ) => ReturnType<(typeof blockBaseOpsInternal)['list']>
} = {
  delete: (tx, ...params) => {
    invariantBaseHubTransaction(tx)
    return blockBaseOpsInternal.delete(tx, ...params)
  },
  get: (tx, key, ...rest) => {
    invariantBaseHubTransaction(tx)
    return blockBaseOpsInternal.get(
      tx,
      key,
      // @ts-ignore
      ...rest
    )
  },
  has: (tx, ...params) => {
    invariantBaseHubTransaction(tx)
    return blockBaseOpsInternal.has(tx, ...params)
  },
  init: (tx, ...params) => {
    invariantBaseHubTransaction(tx)
    return blockBaseOpsInternal.init(tx, ...params)
  },
  list: async (tx, ...params) => {
    invariantBaseHubTransaction(tx)

    // ensure rootBlockId is being sent
    console.log('128')
    getEntriesOptionsSchema.parse(params[0].getEntriesOptions)

    const res = await blockBaseOpsInternal.list(tx, ...(params as any))
    res.forEach((b) => {
      if (tx.blockCache.has(b.id)) return // already in cache, prioritize dirty
      tx.blockCache.set(b.id, b)
    })

    return res
  },
  put: async (tx, value, parentKey) => {
    invariantBaseHubTransaction(tx)
    const parsed = blockSchema.parse(value)
    const key = `block/${parsed.id}`
    return await tx.put(key, parsed, parentKey)
  },
  update: async (tx, value, parentKey) => {
    invariantBaseHubTransaction(tx)
    if (typeof value.id !== 'string') {
      throw new Error(
        `id is required when updating an entry. received: ${value}`
      )
    }

    const key = `block/${value.id}`
    const prev = await tx.get(key)
    if (!prev) {
      throw new Error(`Attempting to update a not found entry: ${key}`)
    }

    return await tx.put(
      key,
      blockSchema.parse({ ...(prev as Block), ...value }),
      parentKey
    )
  },
}

type BaseHubTransactionMeta = {
  __hasBaseHubTransaction: boolean
  deletedInTxSet: Set<string>
  copyOnWriteCache: Map<string, string>
  deletedInstanceChildByTargetBlockIdCache: Map<
    string,
    { block: Block; br: BlockRelationship }
  >
  treeManagerCache: Map<string, TreeManager<Block, CommonBlocksCompoundIndexes>>
  blockCache: IndexedMap<Block>
  blockRelationshipCache: IndexedMap<
    BlockRelationship,
    {
      byBlockOrSourceBlockId: IndexedMapCompoundIndex<
        BlockRelationship,
        Map<string, Set<string>>
      >
    }
  >
  _brsByRootId: IndexedMap<
    IndexedMap<BlockRelationship, CommonBRsCompoundIndexes>
  >
  getReferencesByTargetCache: Map<string, Block[]>
  currentTreeManager?: TreeManager<Block, CommonBlocksCompoundIndexes>
  listTreeEntries: (rootId: string) => Promise<(Block | BlockRelationship)[]>
  getTreeManager: (
    rootId: string,
    opts?: { ignoreTxCache?: boolean; ignoreCopyOnWrite?: boolean }
  ) => Promise<TreeManager<Block, CommonBlocksCompoundIndexes>>
  getBlockChildren: (opts: {
    sourceBlockId: string
    rootBlockId: string
    includeDeleted?: boolean
  }) => Promise<BlockRelationship[]>
  getBlockRelationshipByBlockId: (opts: {
    blockId: string
    rootBlockId: string
    includeDiscarded?: boolean
  }) => Promise<BlockRelationship | null>
  getReferencesByTarget: (options: {
    targetBlockId: string
    rootBlockId: string
  }) => Promise<Block[]>
  getInstancesOfComponent: (options: {
    componentSourceBlockId: string
    rootBlockId: string
  }) => Promise<Block[]>
  getBrsByRootId: (
    rootBlock: RootBlock
  ) => Promise<IndexedMap<BlockRelationship, CommonBRsCompoundIndexes>>
  revalidateBlockMilestonesMap: Map<string, RevalidateBlockMilestoneParams>
}

export type BaseHubTransaction = WriteTransaction &
  BaseHubTransactionMeta & {
    put: (
      key: string,
      value: ReadonlyJSONObject,
      parentKey?: string | null
    ) => ReturnType<WriteTransaction['put']>
    set: (
      key: string,
      value: ReadonlyJSONObject,
      parentKey?: string
    ) => ReturnType<WriteTransaction['set']>
    get: <T extends ReadonlyJSONObject>(
      key: string,
      opts?: { acceptDeleted?: boolean }
    ) => Promise<T | undefined>
    serverOnly?: ServerOnlyParams
    clientOnly?: ClientOnlyParams
    serverSecret?: string
    flush?: () => Promise<{
      del: schema.Entry[]
      ins: schema.Entry[][]
    }>
  }

export function invariantBaseHubTransaction(
  tx:
    | ReadTransaction
    | WriteTransaction
    | ReplicacheTransaction
    | BaseHubTransaction
): asserts tx is BaseHubTransaction {
  invariant(
    '__hasBaseHubTransaction' in tx && tx.__hasBaseHubTransaction,
    'tx is not a BaseHubTransaction'
  )
}

export function ensureBaseHubTransaction(
  tx:
    | ReadTransaction
    | WriteTransaction
    | ReplicacheTransaction
    | BaseHubTransaction
): asserts tx is BaseHubTransaction {
  if ('__hasBaseHubTransaction' in tx && tx.__hasBaseHubTransaction) return

  if (
    ('serverOnly' in tx === false || !tx.serverOnly) &&
    ('clientOnly' in tx === false || !tx.clientOnly)
  ) {
    // hydrate serverOnly with client-side context
    // @ts-ignore
    tx.clientOnly = transactionClientContext.getState().get(tx.clientID)
    // @ts-ignore
    if (tx.clientOnly) {
      // @ts-ignore
      tx.clientOnly.passedFirstMutatorCheck = false
    }
  }

  const current = {
    ...tx,
    get: tx.get,
    has: tx.has,
    del: (tx as WriteTransaction).del,
    set: (tx as WriteTransaction).set,
    put: (tx as WriteTransaction).put,
  }

  let h: ReturnType<ServerOnlyParams['hydrateRepo']> | undefined
  if ('serverOnly' in tx && tx.serverOnly) {
    h = tx.serverOnly.hydrateRepo()
  }

  const basehubTransaction: BaseHubTransactionMeta = {
    __hasBaseHubTransaction: true,
    revalidateBlockMilestonesMap: new Map(),
    copyOnWriteCache: new Map(),
    deletedInTxSet: new Set(),
    treeManagerCache: new Map(),
    blockCache: h?.blockCache ?? createIndexedMap<Block>(),
    blockRelationshipCache:
      h?.blockRelationshipCache ??
      createIndexedMap({
        onChange: ({ item: br, operation }) => {
          for (const rootId of basehubTransaction._brsByRootId.keys()) {
            const existingIndex = basehubTransaction._brsByRootId.get(rootId)
            const existingRoot = basehubTransaction.blockCache.get(rootId)
            invariant(
              existingRoot && isRootBlock(existingRoot),
              `root ${rootId} not found`
            )
            if (isBRInRoot(br, existingRoot)) {
              if (operation === 'upsert') {
                // If we have an existing index for this root, update it
                if (existingIndex) {
                  existingIndex.set(br.id, br)
                }
                // Ignore - index will be created on first access
              } else if (operation === 'delete') {
                // If we have an existing index, remove the BR from it
                existingIndex?.delete(br.id)
              }
            } else {
              existingIndex?.delete(br.id)
            }
          }
        },
        compoundIndexes: {
          byBlockOrSourceBlockId:
            commonBRsCompoundIndexes.byBlockOrSourceBlockId,
        },
      }),
    _brsByRootId:
      h?._brsByRootId ??
      createIndexedMap<
        IndexedMap<BlockRelationship, CommonBRsCompoundIndexes>
      >(),
    async getBrsByRootId(rootBlock) {
      const existing = this._brsByRootId.get(rootBlock.id)
      if (existing) return existing

      if (h) {
        const brs = (await h.getBrsByRootId(rootBlock)).copyOnWrite()
        this._brsByRootId.set(rootBlock.id, brs)
        return brs
      }

      const newMap = createIndexedMap<
        BlockRelationship,
        CommonBRsCompoundIndexes
      >({
        autoFallbackKeys: ['sourceBlockId', 'blockId'],
        compoundIndexes: commonBRsCompoundIndexes,
        onIndexConflict: getOnIndexConflictHandler(() => rootBlock),
      })

      for (const br of this.blockRelationshipCache.values()) {
        if (isBRInRoot(br, rootBlock)) {
          newMap.set(br.id, br)
        }
      }

      this._brsByRootId.set(rootBlock.id, newMap)
      return newMap
    },
    getReferencesByTargetCache: new Map(),
    deletedInstanceChildByTargetBlockIdCache: new Map(),
    currentTreeManager: undefined,
    async listTreeEntries(rootBlockId) {
      const result: (Block | BlockRelationship)[] = []
      if ('serverOnly' in tx && tx.serverOnly) {
        const executor = tx.serverOnly.executor as any as DB

        const query = getNestedBlocksSQL({
          spaceLineage: tx.serverOnly.spaceLineage ?? [tx.serverOnly.spaceID],
          cvrKey: null,
          cvrSyncSequence: null,
          rootBlockId,
          mode: 'both',
        })

        const existing =
          await executor.execute<Entry<Block | BlockRelationship>>(query)

        existing.forEach((entry) => {
          if (entry.key.startsWith('br/')) {
            if (
              !basehubTransaction.blockRelationshipCache.has(
                entry.key.replace('br/', '')
              )
            ) {
              const br = entry.value as BlockRelationship
              basehubTransaction.blockRelationshipCache.set(br.id, br)
            }
          } else {
            if (
              !basehubTransaction.blockCache.has(
                entry.key.replace('block/', '')
              )
            ) {
              const block = entry.value as Block
              basehubTransaction.blockCache.set(block.id, block)
            }
          }
        })
      } else {
        const rootBlock = await blockBaseOpsInternal.get(tx, rootBlockId)
        invariant(rootBlock, `root block ${rootBlockId} not found`)
        invariant(
          rootBlock.type === 'root',
          `root block ${rootBlockId} is not a root block`
        )

        for await (const value of tx.scan({ prefix: 'br/' })) {
          const br = value as BlockRelationship
          if (!isBRInRoot(br, rootBlock)) continue
          const block = await blockBaseOpsInternal.get(tx, br.blockId)
          if (!block) continue
          if (!basehubTransaction.blockCache.has(block.id)) {
            basehubTransaction.blockCache.set(block.id, block)
          }
          if (!basehubTransaction.blockRelationshipCache.has(br.id)) {
            basehubTransaction.blockRelationshipCache.set(br.id, br)
          }
        }
      }

      const rootBlock = basehubTransaction.blockCache.get(rootBlockId)
      invariant(
        rootBlock && isRootBlock(rootBlock),
        `root block ${rootBlockId} not found`
      )
      const brs = await basehubTransaction.getBrsByRootId(rootBlock)
      for (const br of brs.values()) {
        result.push(br)
        const block = basehubTransaction.blockCache.get(br.blockId)
        if (!block) continue
        result.push(block)
      }

      return result
    },
    async getBlockChildren({
      sourceBlockId,
      rootBlockId,
      includeDeleted = false,
    }: {
      sourceBlockId: string
      rootBlockId: string
      includeDeleted?: boolean
    }) {
      const rootBlock = (await tx.get(`block/${rootBlockId}`)) as
        | RootBlock
        | undefined
      invariant(
        rootBlock && isRootBlock(rootBlock),
        `root block ${rootBlockId} not found`
      )

      const brs = await basehubTransaction.getBrsByRootId(rootBlock)

      if (!includeDeleted && 'serverOnly' in tx && tx.serverOnly) {
        const cached = brs.compoundIndexes.get('byParentId')?.get(sourceBlockId)
        if (!includeDeleted) {
          return Array.from(cached ?? [])
            .map((id) => {
              const br = brs.get(id)
              invariant(br, `block relationship ${id} not found in cache`)
              if (br.deletedAt) return null
              return br
            })
            .filter(Boolean)
            .sort(compareOrder)
        }
      }

      if ('serverOnly' in tx && tx.serverOnly) {
        const db = tx.serverOnly.executor as any as DB
        const spaces =
          tx.serverOnly.spaceLineage?.map((space: string) => sql`${space}`) ??
          []
        const allRelationships = await db.execute<Entry<BlockRelationship>>(
          sql`
               WITH space_lineage AS (
                -- Create a table with spaces and their priorities based on position in lineage
                SELECT 
                  space_id,
                  ordinality AS priority
                FROM unnest(ARRAY[${sql.join(
                  spaces,
                  sql`,`
                )}]) WITH ORDINALITY AS t(space_id, ordinality)
              ),
              root_data AS (
                -- Get root block with branch info and timestamps
                SELECT DISTINCT ON (key)
                  key as root_key,
                  value->>'id' as root_id,
                  value->>'branchId' as branch_id,
                  value->'branchLineage' as branch_lineage,
                  (value->>'createdAt')::timestamptz as created_at,
                  value->>'isCommitted' = 'true' as is_committed,
                  value->>'isInlineSuggestion' as is_inline_suggestion
                FROM entry
                JOIN space_lineage sl ON sl.space_id = entry.spaceid
                WHERE key = ${`block/${rootBlockId}`} 
                  AND spaceid IN (${sql.join(spaces, sql`,`)})
                ORDER BY key, sl.priority
                LIMIT 1
              ),
              -- Expand branch lineage to include current branch and ancestors
              branch_lineage AS (
                SELECT 
                  t.branch_id,
                  t.last_synced_at,
                  t.ordinality AS priority
                FROM root_data,
                LATERAL unnest(
                  ARRAY[root_data.branch_id] || 
                  CASE 
                    WHEN root_data.branch_lineage IS NULL THEN ARRAY[]::text[]
                    ELSE ARRAY(SELECT jsonb_array_elements(root_data.branch_lineage)->>'branchId')
                  END,
                  ARRAY[NULL] || 
                  CASE 
                    WHEN root_data.branch_lineage IS NULL THEN ARRAY[]::text[]
                    ELSE ARRAY(SELECT jsonb_array_elements(root_data.branch_lineage)->>'lastSyncedAt')
                  END
                ) WITH ORDINALITY AS t(branch_id, last_synced_at, ordinality)
              ),
              -- Get all relevant block relationships
              possible_brs AS (
                SELECT DISTINCT ON (e.value->>'sourceBlockId') *
                FROM branch_lineage bl
                JOIN entry e ON (
                  -- Match the branch ID
                  e.value->>'branchId' = bl.branch_id
  
                  -- Root committed check
                  AND (
                    NOT (SELECT is_committed FROM root_data)
                    OR e.value->>'isCommitted' = 'true'
                  )
  
                  -- Not discarded
                  AND e.value->>'discardedAt' IS NULL
  
                  -- Must have specified parent
                  AND e.value->>'parentId' = ${sourceBlockId}
  
                  -- Additional branch-specific checks
                  AND (
                    -- If it's current branch, no additional check needed
                    (bl.priority = 1)
                    
                    -- For ancestor branches
                    OR 
                    (
                      bl.priority > 1 
                      -- Must be committed for ancestor branches (or inline suggestion but not leaked from other inline suggestion-branches)
                      AND (e.value->>'isCommitted' = 'true' OR ((SELECT is_inline_suggestion FROM root_data) = 'true' and e.value->>'isInlineSuggestion' is null))
                      -- BR was valid at checkout time
                      AND (e.value->>'validFrom')::timestamptz <= bl.last_synced_at::timestamptz
                      AND (e.value->>'validTo' IS NULL OR (e.value->>'validTo')::timestamptz >= bl.last_synced_at::timestamptz)
                    )
                  )
                )
                JOIN space_lineage sl ON sl.space_id = e.spaceid
                WHERE e.key LIKE 'br/%'
                  AND e.spaceid IN (${sql.join(spaces, sql`,`)})
                  -- ALWAYS check if BR is valid at root creation time
                  AND (e.value->>'validFrom')::timestamptz <= (SELECT created_at FROM root_data)
                  -- Don't perform validTo check YET, as we want to make the distinct on preserve overrides
                  -- AND (e.value->>'validTo' IS NULL OR (e.value->>'validTo')::timestamptz >= (SELECT created_at FROM root_data))
                ORDER BY
                  e.value->>'sourceBlockId',
                  CASE WHEN e.value->>'blockId' = (SELECT root_id FROM root_data) THEN 1 ELSE 0 END DESC,
                  sl.priority,
                  bl.priority,
                  (e.value->>'validTo')::timestamptz DESC NULLS FIRST,
                  (e.value->>'isCommitted')::boolean,
                  (e.value->>'validFrom')::timestamptz DESC
              ),
              -- Filter out by validTo now:
              prioritized_entries AS (
                SELECT *
                FROM possible_brs br
                WHERE (br.value->>'validTo' IS NULL OR (br.value->>'validTo')::timestamptz >= (SELECT created_at FROM root_data)::timestamptz)
              )
              SELECT * FROM prioritized_entries
              ${
                !includeDeleted
                  ? sql.raw(
                      `WHERE ((value->'deletedAt') IS NULL OR (value->'deletedAt')::text = 'null')`
                    )
                  : sql.raw('')
              }
            `
        )
        allRelationships.forEach((r) => {
          basehubTransaction.blockRelationshipCache.set(r.value.id, r.value)
        })
      } else {
        for await (const value of tx.scan({
          indexName: 'brsByParentId',
          prefix: sourceBlockId,
        })) {
          const br = value as BlockRelationship
          if (!br.deletedAt || includeDeleted) {
            if (!basehubTransaction.blockRelationshipCache.has(br.id)) {
              basehubTransaction.blockRelationshipCache.set(br.id, br)
            }
          }
        }
      }

      const childBrs =
        brs.compoundIndexes.get('byParentId')?.get(sourceBlockId) ?? []

      return Array.from(childBrs)
        .map((id) => {
          const br = brs.get(id)
          invariant(br, `block relationship ${id} not found in cache`)
          if (!includeDeleted && br.deletedAt) return null
          return br
        })
        .filter(Boolean)
        .sort(compareOrder)
    },
    async getBlockRelationshipByBlockId({
      blockId,
      rootBlockId,
      includeDiscarded = false,
    }: {
      blockId: string
      rootBlockId: string
      includeDiscarded?: boolean
    }) {
      const rootBlock = (await tx.get(`block/${rootBlockId}`)) as
        | RootBlock
        | undefined
      invariant(
        rootBlock && isRootBlock(rootBlock),
        `root block ${rootBlockId} not found`
      )
      const brs = await basehubTransaction.getBrsByRootId(rootBlock)
      const cached = brs.get(blockId)
      if (cached) {
        if (!includeDiscarded && cached.discardedAt) return null
        return cached
      }

      let sourceBlockId = blockId

      if ('serverOnly' in tx && tx.serverOnly) {
        const db = tx.serverOnly.executor as any as DB
        const spaces =
          tx.serverOnly.spaceLineage?.map((space: string) => sql`${space}`) ??
          []
        const query = sql`
              WITH space_lineage AS (
              -- Create a table with spaces and their priorities based on position in lineage
              SELECT 
                space_id,
                ordinality AS priority
              FROM unnest(ARRAY[${sql.join(
                spaces,
                sql`,`
              )}]) WITH ORDINALITY AS t(space_id, ordinality)
            ),
            root_data AS (
              -- Get root block with branch info and timestamps
              SELECT DISTINCT ON (key)
                key as root_key,
                value->>'id' as root_id,
                value->>'branchId' as branch_id,
                value->'branchLineage' as branch_lineage,
                (value->>'createdAt')::timestamptz as created_at,
                value->>'isCommitted' = 'true' as is_committed,
                value->>'isInlineSuggestion' as is_inline_suggestion
              FROM entry
              JOIN space_lineage sl ON sl.space_id = entry.spaceid
              WHERE key = ${`block/${rootBlockId}`} 
                AND spaceid IN (${sql.join(spaces, sql`,`)})
              ORDER BY key, sl.priority
              LIMIT 1
            ),
            -- Expand branch lineage to include current branch and ancestors
            branch_lineage AS (
              SELECT 
                t.branch_id,
                t.last_synced_at,
                t.ordinality AS priority
              FROM root_data,
              LATERAL unnest(
                ARRAY[root_data.branch_id] || 
                CASE 
                  WHEN root_data.branch_lineage IS NULL THEN ARRAY[]::text[]
                  ELSE ARRAY(SELECT jsonb_array_elements(root_data.branch_lineage)->>'branchId')
                END,
                ARRAY[NULL] || 
                CASE 
                  WHEN root_data.branch_lineage IS NULL THEN ARRAY[]::text[]
                  ELSE ARRAY(SELECT jsonb_array_elements(root_data.branch_lineage)->>'lastSyncedAt')
                END
              ) WITH ORDINALITY AS t(branch_id, last_synced_at, ordinality)
            ),
            -- Get all relevant block relationships
            possible_brs AS (
              SELECT DISTINCT ON (e.value->>'sourceBlockId') *
              FROM branch_lineage bl
              JOIN entry e ON (
                -- Match the branch ID
                e.value->>'branchId' = bl.branch_id
  
                -- Root committed check
                AND (
                  NOT (SELECT is_committed FROM root_data)
                  OR e.value->>'isCommitted' = 'true'
                )
  
                ${
                  includeDiscarded
                    ? sql.raw('')
                    : sql.raw(`AND e.value->>'discardedAt' IS NULL`)
                }
                
                -- Must match blockId or sourceBlockId
                AND (e.value->>'blockId' = ${blockId} OR e.value->>'sourceBlockId' = ${blockId})
  
                -- Additional branch-specific checks
                AND (
                  -- If it's current branch, no additional check needed
                  (bl.priority = 1)
                  
                  -- For ancestor branches
                  OR 
                  (
                    bl.priority > 1 
                    -- Must be committed for ancestor branches (or inline suggestion but not leaked from other inline suggestion-branches)
                    AND (e.value->>'isCommitted' = 'true' OR ((SELECT is_inline_suggestion FROM root_data) = 'true' and e.value->>'isInlineSuggestion' is null))
                    -- BR was valid at checkout time
                    AND (e.value->>'validFrom')::timestamptz <= bl.last_synced_at::timestamptz
                    AND (e.value->>'validTo' IS NULL OR (e.value->>'validTo')::timestamptz >= bl.last_synced_at::timestamptz)
                  )
                )
              )
              JOIN space_lineage sl ON sl.space_id = e.spaceid
              WHERE e.key LIKE 'br/%'
                AND e.spaceid IN (${sql.join(spaces, sql`,`)})
                -- ALWAYS check if BR is valid at root creation time
                AND (e.value->>'validFrom')::timestamptz <= (SELECT created_at FROM root_data)
                -- Don't perform validTo check YET, as we want to make the distinct on preserve overrides
                -- AND (e.value->>'validTo' IS NULL OR (e.value->>'validTo')::timestamptz >= (SELECT created_at FROM root_data))
  
              ORDER BY
                e.value->>'sourceBlockId',
                CASE WHEN e.value->>'blockId' = (SELECT root_id FROM root_data) THEN 1 ELSE 0 END DESC,
                ${
                  includeDiscarded
                    ? sql.raw(
                        `CASE WHEN e.value->>'discardedAt' IS NULL THEN 0 ELSE 1 END,`
                      )
                    : sql.raw('')
                }
                sl.priority,
                bl.priority,
                (e.value->>'validTo')::timestamptz DESC NULLS FIRST,
                (e.value->>'isCommitted')::boolean,
                (e.value->>'validFrom')::timestamptz DESC
            ),
            -- Filter out by validTo now:
            prioritized_entries AS (
              SELECT *
              FROM possible_brs br
              WHERE (br.value->>'validTo' IS NULL OR (br.value->>'validTo')::timestamptz >= (SELECT created_at FROM root_data)::timestamptz)
            )
            SELECT * FROM prioritized_entries
            LIMIT 1`
        const allRelationships =
          await db.execute<Entry<BlockRelationship>>(query)
        allRelationships.forEach((entry, i) => {
          if (i > 0) {
            throw new Error(
              'multiple block relationships found? limit should be 1'
            )
          }
          basehubTransaction.blockRelationshipCache.set(
            entry.value.id,
            entry.value
          )
          sourceBlockId = entry.value.sourceBlockId
        })
      } else {
        let found = false

        if (!found) {
          for await (const value of tx.scan({
            indexName: 'brsByBlockId',
            prefix: blockId,
          })) {
            const br = value as BlockRelationship
            if (!basehubTransaction.blockRelationshipCache.has(br.id)) {
              basehubTransaction.blockRelationshipCache.set(br.id, br)
            }
            if (isBRInRoot(br, rootBlock)) {
              found = true
              sourceBlockId = br.sourceBlockId
              break
            }
          }
        }

        if (!found) {
          for await (const value of tx.scan({
            indexName: 'brsBySourceBlockId',
            prefix: blockId,
          })) {
            const br = value as BlockRelationship
            if (!basehubTransaction.blockRelationshipCache.has(br.id)) {
              basehubTransaction.blockRelationshipCache.set(br.id, br)
            }
            if (isBRInRoot(br, rootBlock)) {
              found = true
              sourceBlockId = br.sourceBlockId
              break
            }
          }
        }
      }

      let result = brs.get(blockId)
      if (!result) {
        const process = (br: BlockRelationship) => {
          if (
            isBRInRoot(br, rootBlock, {
              omitDiscardedAtComparison: includeDiscarded,
            }) === false
          ) {
            return
          }
          if (!result) {
            result = br
            return
          }
          if (
            isBRMoreImportant(br, result, rootBlock, {
              omitDiscardedAtComparison: includeDiscarded,
              blockIdHint: blockId,
            })
          ) {
            result = br
          }
        }

        const altKeysSet =
          basehubTransaction.blockRelationshipCache.compoundIndexes
            .get('byBlockOrSourceBlockId')
            ?.get(blockId)

        if (altKeysSet?.size) {
          for (const altKey of altKeysSet) {
            const altBr = basehubTransaction.blockRelationshipCache.get(altKey)
            if (!altBr) continue
            process(altBr)
          }
        }

        if (brs.has(sourceBlockId)) {
          process(brs.get(sourceBlockId)!)
        }
      }

      if (!includeDiscarded && result?.discardedAt) {
        return null
      }

      return result
    },
    async getTreeManager(rootId, opts) {
      if (!opts?.ignoreTxCache) {
        const cached =
          this.treeManagerCache.get(rootId) ??
          basehubTransaction.treeManagerCache.get(rootId)
        if (cached) {
          this.currentTreeManager = cached
          basehubTransaction.currentTreeManager = cached
          return cached
        }
      }

      if (h) {
        const tm = await h.getTreeManager(rootId, {
          ignoreCopyOnWrite: opts?.ignoreCopyOnWrite,
        })
        if (!opts?.ignoreTxCache) {
          this.currentTreeManager = tm
          basehubTransaction.currentTreeManager = tm
          this.treeManagerCache.set(rootId, tm)
          basehubTransaction.treeManagerCache.set(rootId, tm)
        }
        return tm
      }

      if ('serverOnly' in tx) {
        throw new Error('expected tx to be hydrated')
      }

      const tm = await basehubTransaction
        .listTreeEntries(rootId)
        .then(async () => {
          const rootBlock = basehubTransaction.blockCache.get(rootId)
          if (!rootBlock) {
            throw new Error('root block not found')
          }
          if (rootBlock.type !== 'root') {
            throw new Error('root block is not a root block')
          }
          const brs = await basehubTransaction.getBrsByRootId(rootBlock)

          return getTreeManager<Block, CommonBlocksCompoundIndexes>({
            rootKey: rootId,
            getValue: (key) => {
              const block = basehubTransaction.blockCache.get(key)
              invariant(block, `block ${key} not found in cache`)
              return block
            },
            getChildrenKeys: (_key, block) => {
              return getChildrenKeys({
                block,
                blockRelationshipsMap: brs,
                blocksMap: basehubTransaction.blockCache,
                rootBlockId: rootId,
              })
            },
            getOrder: (key, value) => {
              const br = brs.get(key) ?? brs.get(idToSourceBlockId(value))
              invariant(br, `block relationship ${key} not found in cache`)
              return br.order
            },
            getKey: (block) => block.id,
            compoundIndexes: commonBlocksCompoundIndexes,
            indexKeys: ['sourceBlockId'],
            autoFallbackWithKeys: ['sourceBlockId'],
            onChange: (props) => {
              const handler = getOnChangeHandler({
                blockRelationshipsMap: brs,
                blocksMap: basehubTransaction.blockCache,
              })
              return handler(props)
            },
          })
        })

      basehubTransaction.treeManagerCache.set(rootId, tm)
      this.currentTreeManager = tm
      basehubTransaction.currentTreeManager = tm

      return tm
    },
    async getReferencesByTarget({
      targetBlockId,
      rootBlockId,
    }: {
      targetBlockId: string
      rootBlockId: string
    }) {
      const result: Block[] = []
      const tm = await basehubTransaction.getTreeManager(rootBlockId)
      const referencesByTarget = tm.compoundIndexes.get('referencesByTarget')
      invariant(referencesByTarget, 'referencesByTarget not found')
      const refs = referencesByTarget.get(targetBlockId)
      refs?.forEach((ref) => {
        result.push(tm.getNode(ref, { required: true }).value)
      })
      return result
    },
    async getInstancesOfComponent({
      componentSourceBlockId,
      rootBlockId,
    }: {
      componentSourceBlockId: string
      rootBlockId: string
    }) {
      const result: Block[] = []
      if ('serverOnly' in tx && tx.serverOnly) {
        const executor = tx.serverOnly.executor as any as DB

        const query = getNestedBlocksSQL({
          spaceLineage: tx.serverOnly.spaceLineage ?? [tx.serverOnly.spaceID],
          cvrKey: null,
          cvrSyncSequence: null,
          rootBlockId,
          mode: 'blockOnly',
          additionalFilters: (blockKey) => sql`
            ${sql.raw(
              blockKey
            )}.value->'typeOptions'->'targetBlockId' = ${componentSourceBlockId}
          `,
        })

        const existing = await executor.execute(query)
        existing.forEach((entry) => {
          result.push(entry.value as Block)
        })
      } else {
        const tm = await basehubTransaction.getTreeManager(rootBlockId)
        const componentInstances = tm.compoundIndexes.get('componentInstances')
        invariant(componentInstances, 'componentInstances not found')
        const refs = componentInstances.get(componentSourceBlockId)
        refs?.forEach((ref) => {
          result.push(tm.getNode(ref, { required: true }).value)
        })
      }

      return result
    },
  }

  tx.get = async (key: string, ...rest) => {
    if (key.startsWith('block/')) {
      if (basehubTransaction.blockCache) {
        const blockId = key.split('/')[1]
        invariant(blockId, 'blockId could not be found')

        const has = basehubTransaction.blockCache.has(blockId)
        if (has) {
          const res = basehubTransaction.blockCache.get(blockId)!
          return res
        }
      }
    } else if (key.startsWith('br/')) {
      const brId = key.split('/')[1]
      invariant(brId, 'brId could not be found')

      const has = basehubTransaction.blockRelationshipCache.has(brId)
      if (has) {
        const res = basehubTransaction.blockRelationshipCache.get(brId)!
        return res
      }
    }

    const entry = await current.get(key, ...rest)

    if (key.startsWith('block/')) {
      if (entry && basehubTransaction.blockCache) {
        const block = entry as Block
        basehubTransaction.blockCache.set(block.id, block)
      }
    } else if (key.startsWith('br/')) {
      if (entry && basehubTransaction.blockRelationshipCache) {
        const br = entry as BlockRelationship
        basehubTransaction.blockRelationshipCache.set(br.id, br)
      }
    }

    return entry
  }

  if ('del' in tx) {
    tx.del = async (key: string) => {
      const id = key.split('/')[1]
      invariant(id, `entry id could not be found for key ${key}`)
      basehubTransaction.deletedInTxSet.add(id)

      if (!key.startsWith('block/')) {
        return await current.del(key)
      }

      if (basehubTransaction.blockCache) {
        const existing = basehubTransaction.blockCache.get(id)
        if (existing?.isCommitted) {
          throw new Error(`Attempting to delete a committed block ${id}`)
        }
        if (existing) basehubTransaction.blockCache.delete(id)
      }

      /**
       * We don't delete from the copyOnWriteCache because, if this blocks attempts to revive,
       * then it will be copied on written again, and why not just match this version?
       * if (basehubTransaction.copyOnWriteCache) {
       *   if (basehubTransaction.copyOnWriteCache.has(id)) {
       *     basehubTransaction.copyOnWriteCache.delete(id)
       *   }
       * }
       */

      return await current.del.call(tx, key)
    }

    const upsertBlockInTxCache = (key: string, block: Block) => {
      key = key.startsWith('block/') ? key.replace('block/', '') : key

      if (isVirtualInstanceChildTarget(block)) {
        throw new Error(
          `Attempting to create/update a virtualized block ${key}`
        )
      }

      if (basehubTransaction.blockCache) {
        const existing = basehubTransaction.blockCache.get(key)
        if (existing) {
          if (existing.isCommitted) {
            throw new Error(`Attempting to update a committed block ${key}`)
          }
        }
        basehubTransaction.blockCache.set(block.id, block)
      }
    }

    tx.set = async (
      key: string,
      value: ReadonlyJSONValue,
      _parentKey?: string
    ) => {
      const id = key.split('/')[1] as string
      /** Read note at the bottom of this file for more info */
      const isDryRunMode = id && basehubTransaction.deletedInTxSet.has(id)
      if (!isDryRunMode) {
        await current.set(key, value)
      }
      if (key.startsWith('block/')) {
        upsertBlockInTxCache(key, value as Block)
      } else if (key.startsWith('br/')) {
        if (basehubTransaction.blockRelationshipCache) {
          basehubTransaction.blockRelationshipCache.set(
            id,
            value as BlockRelationship
          )
        }
      }
    }

    tx.put = tx.set
  }

  tx.has = async (key: string) => {
    if (key.startsWith('block/')) {
      const blockId = key.split('/')[1]
      invariant(blockId, 'blockId could not be found')

      if (
        basehubTransaction.blockCache &&
        basehubTransaction.blockCache.has(blockId)
      ) {
        return true
      }
    } else if (key.startsWith('br/')) {
      const brId = key.split('/')[1]
      invariant(brId, 'brId could not be found')

      if (
        basehubTransaction.blockRelationshipCache &&
        basehubTransaction.blockRelationshipCache.has(brId)
      ) {
        return true
      }
    }

    return await current.has(key)
  }

  if ('flush' in tx) {
    tx.flush = async () => {
      if (basehubTransaction.revalidateBlockMilestonesMap.size) {
        console.log(
          `revalidating ${basehubTransaction.revalidateBlockMilestonesMap.size} block milestones`
        )
        await pMap(
          basehubTransaction.revalidateBlockMilestonesMap,
          async ([_key, params]) => {
            ensureBaseHubTransaction(tx)
            await revalidateBlockMilestone(tx, params)
          }
        )
        basehubTransaction.revalidateBlockMilestonesMap.clear()
      }

      // @ts-ignore
      const cache = [...tx._cache.entries()]
      // @ts-ignore
      const storage = tx._storage
      const deletions = []
      const insertions = []

      for (const [k, { dirty, value }] of cache) {
        if (!dirty) continue
        const id = k.split('/')[1]
        if (
          value === undefined ||
          (id && basehubTransaction.deletedInTxSet.has(id))
        ) {
          deletions.push(k)
        } else {
          insertions.push({ key: k, value })
        }
      }

      const [del, ins] = await Promise.all([
        deletions.length ? storage.delEntries(deletions) : Promise.resolve([]),
        insertions.length
          ? storage.putEntries(insertions)
          : Promise.resolve([]),
      ])

      return { del, ins }
    }
  }

  tx = Object.assign(tx, basehubTransaction)
}

/**
 * Note on copy-on-write + deletes + blocks being revived:
 * With copy-on-write, there's a chance that, within a transaction, a child "revives" its parent by copy-on-writing it
 * We'll turn .put and .set into dryRun mode if this happens; don't fail, but just don't add the entry into the cache.
 * This might happen when executing multi-delete blocks that depend on each other. First, one entry gets copy-on-written + deleted,
 * then, another entry which has a dependency on the first one (via a child reference or such) does copy-on-write and brings the
 * deleted one alive. Fine, try to do that; but it won't matter, as the first entry was deleted anyways...
 */
